
import { useState, useEffect } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface AvatarSelectorProps {
  selectedAvatarId: number;
  onSelectAvatar: (avatarId: number) => void;
  className?: string;
}

export function AvatarSelector({ selectedAvatarId, onSelectAvatar, className }: AvatarSelectorProps) {
  const [avatars, setAvatars] = useState<{ id: number, url: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAvatars = async () => {
      setIsLoading(true);
      try {
        // Try to list all avatars from Supabase storage
        const { data: storageData, error: storageError } = await supabase
          .storage
          .from('avatars')
          .list('', { 
            limit: 100,
            sortBy: { column: 'name', order: 'asc' }
          });

        if (storageError || !storageData) {
          // If error fetching from storage, fallback to local avatars
          const localAvatars = Array.from({ length: 21 }, (_, i) => ({
            id: i + 1,
            url: `/avatars/avatar-${i + 1}.png`
          }));
          setAvatars(localAvatars);
        } else {
          // Process the avatars from storage
          const avatarEntries = storageData
            .filter(file => file.name.startsWith('avatar-') && file.name.endsWith('.png'))
            .map(async (file) => {
              // Extract the avatar ID from filename (avatar-123.png -> 123)
              const idMatch = file.name.match(/avatar-(\d+)\.png$/);
              const avatarId = idMatch ? parseInt(idMatch[1]) : 0;
              
              if (!avatarId) return null;

              // Get a signed URL for this avatar
              const { data: urlData } = await supabase
                .storage
                .from('avatars')
                .createSignedUrl(file.name, 60 * 60); // 1 hour expiry
              
              return {
                id: avatarId,
                url: urlData?.signedUrl || `/avatars/avatar-${avatarId}.png`
              };
            });

          // Wait for all URL fetching to complete and filter out any null values
          const resolvedAvatars = (await Promise.all(avatarEntries)).filter(Boolean) as { id: number, url: string }[];
          
          // Sort by ID
          resolvedAvatars.sort((a, b) => a.id - b.id);
          
          // If we have avatars from storage, use them
          if (resolvedAvatars.length > 0) {
            setAvatars(resolvedAvatars);
          } else {
            // Fallback to local avatars
            const localAvatars = Array.from({ length: 21 }, (_, i) => ({
              id: i + 1,
              url: `/avatars/avatar-${i + 1}.png`
            }));
            setAvatars(localAvatars);
          }
        }
      } catch (error) {
        console.error("Error fetching avatars:", error);
        // Fallback to local avatars in case of any error
        const localAvatars = Array.from({ length: 21 }, (_, i) => ({
          id: i + 1,
          url: `/avatars/avatar-${i + 1}.png`
        }));
        setAvatars(localAvatars);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatars();
  }, []);

  return (
    <div className={cn("space-y-4", className)}>
      <h3 className="text-lg font-medium">Choose Your Avatar</h3>
      
      {isLoading ? (
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-2">
          {Array.from({ length: 21 }, (_, i) => (
            <div 
              key={`loading-${i}`}
              className="aspect-square rounded-full animate-pulse bg-muted"
            />
          ))}
        </div>
      ) : (
        <RadioGroup 
          value={selectedAvatarId.toString()} 
          onValueChange={(value) => onSelectAvatar(parseInt(value))}
          className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-2"
        >
          {avatars.map((avatar) => (
            <div key={avatar.id} className="text-center space-y-1">
              <div className="flex items-center justify-center">
                <RadioGroupItem
                  value={avatar.id.toString()}
                  id={`avatar-${avatar.id}`}
                  className="sr-only peer"
                />
                <Label
                  htmlFor={`avatar-${avatar.id}`}
                  className={cn(
                    "relative cursor-pointer overflow-hidden flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all hover:scale-105",
                    selectedAvatarId === avatar.id 
                      ? "border-primary scale-105" 
                      : "border-transparent hover:border-primary/50"
                  )}
                >
                  <img
                    src={avatar.url}
                    alt={`Avatar ${avatar.id}`}
                    className="w-full h-full object-cover"
                  />
                </Label>
              </div>
            </div>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}
